import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  inputId?: string;
  dropActive: boolean;
  onDropActiveChange: (active: boolean) => void;
  onFile: (file: File | undefined | null) => void;
  onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

/** Excel 入出力ステップ2のドラッグ＆ドロップ領域 */
export default function ExcelImportDropzone({
  inputId = 'excelFile',
  dropActive,
  onDropActiveChange,
  onFile,
  onInputChange,
}: Props) {
  const { t } = useTranslation();
  const fileLabel = t('excel_import.excel_file', { defaultValue: 'Excel file' });
  const selectShort = t('excel_import.select_file_short', { defaultValue: 'ファイル選択' });
  const dragDropText = t('excel_import.drag_drop_text', {
    defaultValue: 'Drag and drop a file or click to select',
  });
  const formatHint = t('excel_import.excel_file_format', {
    defaultValue: 'Supports Excel (.xlsx, .xls) or CSV (.csv)',
  });
  const selectTitle = `${dragDropText} · ${formatHint}`;

  const openPicker = () => document.getElementById(inputId)?.click();

  return (
    <div className="mb-2">
      <label htmlFor={inputId} className="visually-hidden">
        {fileLabel}
      </label>
      <div
        role="button"
        tabIndex={0}
        aria-label={selectShort}
        title={selectTitle}
        className={`excel-import-dropzone${dropActive ? ' excel-import-dropzone--active' : ''}`}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDropActiveChange(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDropActiveChange(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDropActiveChange(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDropActiveChange(false);
          onFile(e.dataTransfer.files?.[0]);
        }}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
      >
        <i className="fas fa-cloud-upload-alt fa-lg text-muted mb-1" aria-hidden="true" />
        <p className="mb-0 small">{dragDropText}</p>
        <p className="text-muted small mb-0">{formatHint}</p>
      </div>
      <input
        type="file"
        className="form-control d-none"
        id={inputId}
        name={inputId}
        accept=".xlsx,.xls,.csv"
        onChange={onInputChange}
        aria-label={fileLabel}
      />
    </div>
  );
}
